// src/components/sales/pages/marketing-campaigns/MarketingCampaignModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { XCircle, CheckCircle } from "lucide-react";
import ModalPortal from "../../../shared/ui/ModalPortal";

const CHANNEL_HINTS = ["Email", "WhatsApp", "SMS"];
const TYPE_HINTS = ["Promotional", "Product Update", "Event Invitation", "Re-engagement", "Information"];

const empty = () => ({
  campaignName: "",
  campaignType: "",
  channelType: "",
  subject: "",
  content: "",
  targetSegment: "",
  sendFrequencyHours: "",
  quietHoursStart: "",
  quietHoursEnd: "",
  scheduledDate: "",
  status: "", // edit only
});

// "HH:mm" -> "HH:mm:ss"
const normalizeTime = (t) => {
  if (!t) return undefined;
  return t.length === 5 ? `${t}:00` : t;
};

// "YYYY-MM-DDTHH:mm" -> add seconds
const normalizeLocalDateTime = (v) => {
  if (!v) return undefined;
  return v.length === 16 ? `${v}:00` : v;
};

function toLocalInputValue(isoLike) {
  if (!isoLike) return "";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function MarketingCampaignModal({
  open,
  mode, // "create" | "edit"
  initial,
  onClose,
  onSubmit, // async(dto) => void
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty());
  const [fieldErr, setFieldErr] = useState({}); // { fieldName: "message" }

  const title = useMemo(
    () => (mode === "edit" ? "Edit Campaign" : "Add Campaign"),
    [mode]
  );

  useEffect(() => {
    if (!open) return;

    setFieldErr({});
    if (mode === "edit" && initial) {
      setForm({
        campaignName: initial.campaignName || "",
        campaignType: initial.campaignType || "",
        channelType: initial.channelType || "",
        subject: initial.subject || "",
        content: initial.content || "",
        targetSegment: initial.targetSegment || "",
        sendFrequencyHours: initial.sendFrequencyHours ?? "",
        quietHoursStart: (initial.quietHoursStart || "").slice(0, 5),
        quietHoursEnd: (initial.quietHoursEnd || "").slice(0, 5),
        scheduledDate: toLocalInputValue(initial.scheduledDate),
        status: initial.status || "",
      });
    } else {
      setForm(empty());
    }
  }, [open, mode, initial]);

  const setErrField = (k, v) => setFieldErr((p) => ({ ...p, [k]: v }));

  const change = (k) => (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
    // clear error on edit
    if (fieldErr[k]) setErrField(k, "");
    if ((k === "quietHoursStart" || k === "quietHoursEnd") && fieldErr.quietHours) {
      setErrField("quietHours", "");
    }
  };

  // KEEP DTO LOGIC AS-IS
  const buildDto = () => {
    const dto = {
      campaignName: form.campaignName.trim(),
      campaignType: form.campaignType.trim(),
      channelType: form.channelType.trim(),
      subject: form.subject.trim(),
      content: form.content.trim(),
      targetSegment: form.targetSegment?.trim() || undefined,
      sendFrequencyHours:
        form.sendFrequencyHours === "" ? undefined : Number(form.sendFrequencyHours),
      quietHoursStart: normalizeTime(form.quietHoursStart) || undefined,
      quietHoursEnd: normalizeTime(form.quietHoursEnd) || undefined,
      scheduledDate: normalizeLocalDateTime(form.scheduledDate) || undefined,
    };

    if (mode === "edit") dto.status = form.status?.trim() || undefined;

    // remove undefined
    Object.keys(dto).forEach((k) => dto[k] === undefined && delete dto[k]);
    return dto;
  };

  const validate = () => {
    const e = {};

    if (!form.campaignName?.trim()) e.campaignName = "Campaign name is required";
    if (!form.campaignType?.trim()) e.campaignType = "Campaign type is required";
    if (!form.channelType?.trim()) e.channelType = "Channel type is required";
    if (!form.subject?.trim()) e.subject = "Subject is required";
    if (!form.content?.trim()) e.content = "Content is required";

    // UI-only validation (doesn't change DTO)
    const hasStart = !!form.quietHoursStart;
    const hasEnd = !!form.quietHoursEnd;
    if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
      e.quietHours = "Set both Quiet Hours Start and End, or leave both empty.";
    }

    // numeric validation
    if (form.sendFrequencyHours !== "" && Number.isNaN(Number(form.sendFrequencyHours))) {
      e.sendFrequencyHours = "Send frequency must be a number";
    }

    setFieldErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    const dto = buildDto();

    setSaving(true);
    try {
      console.log("[MarketingCampaignModal] dto =>", dto);
      await onSubmit?.(dto);
    } finally {
      setSaving(false);
    }
  };

  // ✅ return null AFTER all hooks/consts
  if (!open) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onMouseDown={saving ? undefined : onClose}
      />

      {/* Centered modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div
          role="dialog"
          aria-modal="true"
          className="w-full max-w-5xl bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden my-10"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">{title}</h2>
              <p className="text-xs text-white/80 mt-1">
                Required: campaignName, campaignType, channelType, subject, content
              </p>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 disabled:opacity-50"
              disabled={saving}
              title="Close"
            >
              <XCircle className="w-7 h-7" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 max-h-[70vh] overflow-y-auto text-white">
            {!!fieldErr.quietHours && (
              <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                {fieldErr.quietHours}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <Box label="Campaign Name *">
                <input
                  value={form.campaignName}
                  onChange={change("campaignName")}
                  className={inputCls + (fieldErr.campaignName ? errRing : "")}
                  disabled={saving}
                  placeholder="e.g. January Promo"
                />
                {!!fieldErr.campaignName && <ErrText>{fieldErr.campaignName}</ErrText>}
              </Box>

              <Box label="Campaign Type *">
                <input
                  list="campaignTypeList"
                  value={form.campaignType}
                  onChange={change("campaignType")}
                  className={inputCls + (fieldErr.campaignType ? errRing : "")}
                  disabled={saving}
                  placeholder="Select or type"
                />
                <datalist id="campaignTypeList">
                  {TYPE_HINTS.map((x) => (
                    <option key={x} value={x} />
                  ))}
                </datalist>
                {!!fieldErr.campaignType && <ErrText>{fieldErr.campaignType}</ErrText>}
              </Box>

              <Box label="Channel Type *">
                <input
                  list="channelTypeList"
                  value={form.channelType}
                  onChange={change("channelType")}
                  className={inputCls + (fieldErr.channelType ? errRing : "")}
                  disabled={saving}
                  placeholder="Select or type"
                />
                <datalist id="channelTypeList">
                  {CHANNEL_HINTS.map((x) => (
                    <option key={x} value={x} />
                  ))}
                </datalist>
                {!!fieldErr.channelType && <ErrText>{fieldErr.channelType}</ErrText>}
              </Box>

              <Box label="Target Segment">
                <input
                  value={form.targetSegment}
                  onChange={change("targetSegment")}
                  className={inputCls}
                  disabled={saving}
                  placeholder="e.g. Leads, Prospects, Customers..."
                />
                <HintText>Optional. Used for segmentation and reporting.</HintText>
              </Box>

              <Box label="Subject *" className="md:col-span-2">
                <input
                  value={form.subject}
                  onChange={change("subject")}
                  className={inputCls + (fieldErr.subject ? errRing : "")}
                  disabled={saving}
                  placeholder="Email subject / message title"
                />
                {!!fieldErr.subject && <ErrText>{fieldErr.subject}</ErrText>}
              </Box>

              <Box label="Content *" className="md:col-span-2">
                <textarea
                  rows={7}
                  value={form.content}
                  onChange={change("content")}
                  className={inputCls + " leading-relaxed " + (fieldErr.content ? errRing : "")}
                  disabled={saving}
                  placeholder="Write the campaign content here..."
                />
                <div className="mt-2 flex items-center justify-between">
                  {!!fieldErr.content ? <ErrText>{fieldErr.content}</ErrText> : <span />}
                  <div className="text-[11px] text-white/60">
                    {form.content?.length || 0} chars
                  </div>
                </div>
              </Box>

              <Box label="Send Frequency Hours">
                <input
                  type="number"
                  min={0}
                  value={form.sendFrequencyHours}
                  onChange={change("sendFrequencyHours")}
                  className={inputCls + (fieldErr.sendFrequencyHours ? errRing : "")}
                  disabled={saving}
                  placeholder="e.g. 24"
                />
                {!!fieldErr.sendFrequencyHours && <ErrText>{fieldErr.sendFrequencyHours}</ErrText>}
                <HintText>Optional. Enforces frequency rules if backend supports it.</HintText>
              </Box>

              <Box label="Scheduled Date">
                <input
                  type="datetime-local"
                  value={form.scheduledDate}
                  onChange={change("scheduledDate")}
                  className={inputCls}
                  disabled={saving}
                />
                <HintText>Optional. Leave empty to send immediately (if supported).</HintText>
              </Box>

              <Box label="Quiet Hours Start">
                <input
                  type="time"
                  value={form.quietHoursStart}
                  onChange={change("quietHoursStart")}
                  className={inputCls}
                  disabled={saving}
                />
                <HintText>Sent to API as HH:mm:ss</HintText>
              </Box>

              <Box label="Quiet Hours End">
                <input
                  type="time"
                  value={form.quietHoursEnd}
                  onChange={change("quietHoursEnd")}
                  className={inputCls}
                  disabled={saving}
                />
                <HintText>Sent to API as HH:mm:ss</HintText>
              </Box>

              {mode === "edit" && (
                <Box label="Status">
                  <input
                    value={form.status}
                    onChange={change("status")}
                    className={inputCls}
                    disabled={saving}
                    placeholder="e.g. Draft / Scheduled / Sent"
                  />
                  <HintText>Edit-only. Leave blank if backend calculates it.</HintText>
                </Box>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={submit}
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Box({ label, className = "", children }) {
  return (
    <div
      className={
        "bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl " +
        className
      }
    >
      <label className="text-sm text-gray-200">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ErrText({ children }) {
  return <div className="mt-2 text-[11px] text-red-200">{children}</div>;
}

function HintText({ children }) {
  return <div className="mt-2 text-[11px] text-white/60">{children}</div>;
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";

const errRing = " ring-2 ring-red-400/30 border-red-400/30";
